import React, { useState } from 'react';
import DanceStyleSearch from '../../DanceStyleSearch/DanceStyleSearch';
import MonthYearPicker from '../../MonthYearPicker/MonthYearPicker';

// Check if a dance qualifies as "ambi" (both Lead AND Follow at Intermediate or Advanced)
const isAmbiDancer = (dance) => {
  const qualifyingLevels = ['intermediate', 'advanced'];
  const followLevel = dance.follow?.toLowerCase();
  const leadLevel = dance.lead?.toLowerCase();
  return qualifyingLevels.includes(followLevel) && qualifyingLevels.includes(leadLevel);
};

const LEVEL_OPTIONS = [
  { value: '', label: 'Select level' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'not-interested', label: 'Not interested' },
];

const getDefaultSince = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const DancesStep = ({ formData, onNext, onBack, isLastStep, isSubmitting, submitError }) => {
  const [dances, setDances] = useState(formData.dances || []);
  const [currentDance, setCurrentDance] = useState({
    style: '',
    since: getDefaultSince(),
    follow: '',
    lead: '',
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [newsletterConsent, setNewsletterConsent] = useState(formData.newsletterConsent || false);
  const [errors, setErrors] = useState({});
  const [danceErrors, setDanceErrors] = useState({});

  const handleCurrentDanceChange = (field, value) => {
    setCurrentDance((prev) => ({ ...prev, [field]: value }));

    if (danceErrors[field]) {
      setDanceErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateCurrentDance = () => {
    const newErrors = {};

    if (!currentDance.style) {
      newErrors.style = 'Please select a dance style';
    }

    if (!currentDance.since) {
      newErrors.since = 'Please select when you started';
    }

    if (!currentDance.follow && !currentDance.lead) {
      newErrors.level = 'Please select at least one role level';
    }

    if (currentDance.follow === 'not-interested' && currentDance.lead === 'not-interested') {
      newErrors.level = 'You must select a level for at least one role';
    }

    // Check for duplicate dance style
    if (currentDance.style && dances.some(d => d.style.toLowerCase() === currentDance.style.toLowerCase())) {
      newErrors.style = 'This dance style is already added';
    }

    setDanceErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddDance = () => {
    if (!validateCurrentDance()) return;

    setDances((prev) => [...prev, { ...currentDance }]);
    setCurrentDance({
      style: '',
      since: getDefaultSince(),
      follow: '',
      lead: '',
    });
    setDanceErrors({});
  };

  const handleRemoveDance = (index) => {
    setDances((prev) => prev.filter((_, i) => i !== index));
  };

  const formatSinceDate = (since) => {
    if (!since) return '';
    const [year, month] = since.split('-');
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    return `${months[parseInt(month, 10) - 1]} ${year}`;
  };

  const formatLevel = (follow, lead) => {
    const parts = [];
    if (follow && follow !== 'not-interested') {
      parts.push(`Follow: ${follow}`);
    }
    if (lead && lead !== 'not-interested') {
      parts.push(`Lead: ${lead}`);
    }
    return parts.join(' | ');
  };

  const validate = () => {
    const newErrors = {};

    if (dances.length === 0) {
      newErrors.dances = 'Please add at least one dance style';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Auto-add current dance if user filled fields but forgot to click "Add Dance"
    let finalDances = [...dances];
    if (currentDance.style && finalDances.length === 0) {
      const autoAdd = { ...currentDance };

      // Default "since" to current month if not set
      if (!autoAdd.since) {
        autoAdd.since = getDefaultSince();
      }

      // If only one role is set, auto-set the other to 'not-interested'
      if (autoAdd.follow && !autoAdd.lead) {
        autoAdd.lead = 'not-interested';
      } else if (autoAdd.lead && !autoAdd.follow) {
        autoAdd.follow = 'not-interested';
      } else if (!autoAdd.follow && !autoAdd.lead) {
        // No roles set — can't auto-add, let validation catch it
        setDanceErrors({ level: 'Please select at least one role level' });
        return;
      }

      if (autoAdd.follow === 'not-interested' && autoAdd.lead === 'not-interested') {
        setDanceErrors({ level: 'You must select a level for at least one role' });
        return;
      }

      finalDances = [autoAdd];
    }

    // Validate with the final dances list
    if (finalDances.length === 0) {
      setErrors({ dances: 'Please add at least one dance style' });
      return;
    }

    onNext({ dances: finalDances, newsletterConsent });
  };

  return (
    <div className="wizard-step">
      <h2 className="step-title">Your Dance Styles</h2>
      <p className="step-subtitle">Tell us about the dances you love</p>

      <form onSubmit={handleSubmit}>
        {/* Added Dances List */}
        {dances.length > 0 && (
          <div className="added-dances-list">
            {dances.map((dance, index) => {
              const isAmbi = isAmbiDancer(dance);
              return (
                <div key={index} className={`added-dance-item ${isAmbi ? 'ambi' : ''}`}>
                  <div className="dance-info">
                    <span className="dance-style">
                      {dance.style}
                      {isAmbi && <span className="ambi-badge" title="An ambi-dancer here!">☯</span>}
                    </span>
                    <span className="dance-details">
                      Since {formatSinceDate(dance.since)} • {formatLevel(dance.follow, dance.lead)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="remove-dance-btn"
                    onClick={() => handleRemoveDance(index)}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {errors.dances && (
          <div className="field-error" style={{ marginBottom: '1rem' }}>
            {errors.dances}
          </div>
        )}

        {/* Add Dance Form */}
        <div className="add-dance-form">
          <div className="add-dance-header">
            <i className="bi bi-plus-circle"></i>
            <span>Add a Dance Style</span>
          </div>

          {/* Dance Style */}
          <div className="wizard-field">
            <DanceStyleSearch
              value={currentDance.style}
              onChange={(style) => handleCurrentDanceChange('style', style)}
              label="Dance Style"
              placeholder="Search for a dance style..."
              required
              error={danceErrors.style}
            />
          </div>

          {/* Dancing Since */}
          <div className="wizard-field">
            <MonthYearPicker
              value={currentDance.since}
              onChange={(since) => handleCurrentDanceChange('since', since)}
              label="Dancing Since"
              placeholder="Select when you started..."
              required
              error={danceErrors.since}
            />
          </div>

          {/* Follow & Lead Levels */}
          <div className="wizard-field" style={{ marginBottom: '0.5rem' }}>
            <label>Role <span className="required-asterisk">*</span></label>
          </div>
          <div className="wizard-row">
            <div className="wizard-field">
              <label htmlFor="follow">Follow Level</label>
              <select
                id="follow"
                className={`wizard-select ${danceErrors.level ? 'has-error' : ''}`}
                value={currentDance.follow}
                onChange={(e) => handleCurrentDanceChange('follow', e.target.value)}
              >
                {LEVEL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="wizard-field">
              <label htmlFor="lead">Lead Level</label>
              <select
                id="lead"
                className={`wizard-select ${danceErrors.level ? 'has-error' : ''}`}
                value={currentDance.lead}
                onChange={(e) => handleCurrentDanceChange('lead', e.target.value)}
              >
                {LEVEL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {danceErrors.level && <div className="field-error">{danceErrors.level}</div>}

          {/* Add Dance Button */}
          <button
            type="button"
            className="add-dance-btn"
            onClick={handleAddDance}
          >
            <i className="bi bi-plus-lg"></i>
            Add Dance
          </button>
        </div>

        {/* Max dances hint */}
        {dances.length >= 10 && (
          <p className="max-dances-hint">
            <i className="bi bi-info-circle"></i>
            Maximum 10 dance styles allowed
          </p>
        )}

        {/* Terms & Privacy Consent */}
        <div className="wizard-consent-field">
          <label className="wizard-consent-label">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="wizard-consent-checkbox"
            />
            <span className="wizard-consent-text">
              I agree to the{' '}
              <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
            </span>
          </label>
        </div>

        {/* Newsletter Consent */}
        <div className="wizard-consent-field">
          <label className="wizard-consent-label">
            <input
              type="checkbox"
              checked={newsletterConsent}
              onChange={(e) => setNewsletterConsent(e.target.checked)}
              className="wizard-consent-checkbox"
            />
            <span className="wizard-consent-text">
              I'd like to receive Move n Muse newsletters with community insights and updates
            </span>
          </label>
        </div>

        {/* Navigation */}
        <div className="wizard-navigation">
          <button type="button" className="wizard-btn wizard-btn-back" onClick={onBack}>
            <i className="bi bi-arrow-left"></i>
            Back
          </button>
          <button
            type="submit"
            className="wizard-btn wizard-btn-next"
            disabled={isSubmitting || !agreedToTerms}
          >
            {isSubmitting ? (
              <>
                <span className="btn-spinner"></span>
                Creating...
              </>
            ) : (
              <>
                Create Account
                <i className="bi bi-check-lg"></i>
              </>
            )}
          </button>
        </div>

        {/* Submit Error */}
        {submitError && (
          <div className="submit-error">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{submitError}</span>
          </div>
        )}
      </form>

      <style jsx>{`
        .added-dances-list {
          margin-bottom: 1.5rem;
        }

        .added-dance-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          background: linear-gradient(135deg, rgba(232, 197, 71, 0.15) 0%, rgba(212, 175, 55, 0.1) 100%);
          border: 1px solid rgba(212, 175, 55, 0.3);
          border-radius: 10px;
          margin-bottom: 0.5rem;
        }

        .dance-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .added-dance-item.ambi {
          border-left: 3px solid #6B3A5B;
          background: linear-gradient(135deg, rgba(232, 197, 71, 0.2) 0%, rgba(107, 58, 91, 0.1) 100%);
        }

        .dance-style {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          font-weight: 600;
          color: #6B3A5B;
        }

        .ambi-badge {
          font-size: 0.95rem;
          cursor: help;
        }

        .dance-details {
          font-size: 0.75rem;
          color: #555;
        }

        .remove-dance-btn {
          background: none;
          border: none;
          color: #888;
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 50%;
          transition: all 0.2s;
        }

        .remove-dance-btn:hover {
          background: rgba(220, 53, 69, 0.1);
          color: #dc3545;
        }

        .add-dance-form {
          background: #fafafa;
          border-radius: 12px;
          padding: 1.25rem;
          border: 1px dashed #ddd;
        }

        .add-dance-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          color: #6B3A5B;
          margin-bottom: 1rem;
        }

        .add-dance-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          width: 100%;
          padding: 0.75rem;
          background: #6B3A5B;
          border: none;
          border-radius: 10px;
          color: white;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 0.5rem;
        }

        .add-dance-btn:hover {
          background: #5a3050;
        }

        .max-dances-hint {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          font-size: 0.75rem;
          color: #888;
          margin-top: 0.75rem;
        }

        .btn-spinner {
          width: 14px;
          height: 14px;
          border: 2px solid rgba(45, 45, 45, 0.3);
          border-top-color: #2D2D2D;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .submit-error {
          display: flex;
          align-items: flex-start;
          gap: 0.6rem;
          margin-top: 1rem;
          padding: 0.85rem 1rem;
          background: rgba(220, 53, 69, 0.08);
          border: 1px solid rgba(220, 53, 69, 0.2);
          border-radius: 10px;
          color: #dc3545;
          font-size: 0.85rem;
          line-height: 1.4;
        }

        .submit-error i {
          flex-shrink: 0;
          margin-top: 0.1rem;
        }
      `}</style>
    </div>
  );
};

export default DancesStep;
